/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';

import { ThunkExtra } from '@app/store';
import {
  Widget,
  DashboardService,
  GetUserMainLayoutQuery,
} from '@app/gen/dashboard';
import { DynamicEntity } from '@app/types';

import { DashboardState } from '../dashboard.state';

const MARGIN_BOTTOM = 24;

export const getDashboardLayout = createAsyncThunk<
  Widget[],
  GetUserMainLayoutQuery | undefined,
  ThunkExtra
>(
  'dashboard/getDashboard',
  async (dto, { rejectWithValue, extra: { inject } }) => {
    try {
      const result = await inject(DashboardService).getUserMainLayout({
        query: dto,
      });
      return result.widgets;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const resetDashboardLayout = createAction<void>(
  'dashboard/resetDashboard',
);

export const setNewHeight = createAction<{
  name: string;
  height: number;
}>('dashboard/setNewHeight');

export const dashboardDetailReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<DynamicEntity, string> & DashboardState
  >,
) => {
  builder.addCase(getDashboardLayout.rejected, (state, { payload, error }) => {
    state.status = 'failed';
    state.error =
      payload ||
      ({
        info: (error && error.message) || 'Что-то пошло не так',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });
  builder.addCase(getDashboardLayout.fulfilled, (state, { payload }) => {
    const externalIds = payload
      .filter((item) => item.type === 'external')
      .map((item) => item.id);

    externalIds.forEach((id) => {
      state.external = {
        ...state.external,
        [id]: {
          ...state.external[id],
          status: 'idle',
        },
      };
    });
    state.layout = payload;
    state.status = 'complete';
  });
  builder.addCase(getDashboardLayout.pending, (state) => {
    state.status = 'loading';
  });
  builder.addCase(resetDashboardLayout, (state) => {
    state.status = 'idle';
  });
  builder.addCase(setNewHeight, (state, { payload }) => {
    state.layout = state.layout.map((item) =>
      item.id === payload.name
        ? {
            ...item,
            position: {
              ...item.position,
              height: payload.height + MARGIN_BOTTOM,
            },
          }
        : item,
    );
  });
};
